'use client';

/**
 * **A keyed list of rows that animates when one arrives or leaves** (LF-6314).
 *
 * `useRowMotion` is generic and does the hard part, but wiring it correctly is four
 * things a caller has to get right together: the container must be `relative`, each
 * row must be registered under the *same* key `keyOf` returns, the render must walk
 * `rows` rather than the live list (or a departing row unmounts before it can
 * leave), and a leaving row must be made inert. Get any one wrong and the failure is
 * silent — the motion simply does not happen, or a row animates out of the wrong
 * place.
 *
 * Two surfaces need exactly that here — the value confirmation's revealed rows and
 * the instruction sub-form's conditional fields — so it is one component rather than
 * two copies of the same four-part contract.
 *
 * **Why these two surfaces need it at all.** On a grid, motion softens a row
 * *changing*. On both of these, a single click adds or removes **several** rows at
 * once: answering `Confirmed — Yes` reveals three, and choosing an instruction can
 * add a transfer method, a beneficiary list and a contrary-intention panel together.
 * Without motion the whole panel below re-lays out in one frame, which reads as the
 * drawer rebuilding itself rather than as the answer opening a follow-up question.
 * The hook animates *every* row that moved, not only the ones that arrived, which is
 * the half a `slide-in-from-top` on the new rows cannot do (recorded at length in
 * `components/prototypes/form-system/attribute-field.tsx`).
 */

import { ATTRIBUTE_MOTION, useRowMotion } from '@/components/attributes';
import { cn } from '@/lib/utils';

export interface AttributeMotionRow {
  /** Stable across every render that keeps this row. Its identity IS the row. */
  key: string;
  node: React.ReactNode;
  /** Extra classes for the row's wrapper — a bracket rule, an indent. */
  className?: string;
}

export function AttributeMotionRows({
  rows: items,
  className
}: {
  rows: AttributeMotionRow[];
  className?: string;
}) {
  const { containerRef, registerRow, rows } = useRowMotion<AttributeMotionRow>({
    items,
    keyOf: (row) => row.key,
    motion: ATTRIBUTE_MOTION
  });

  return (
    /*
      `relative` is the hook's contract: it absolutely positions a departing row
      against this box. Without it the exit is measured against whichever ancestor
      happens to be positioned, and the row leaves from the wrong place.
    */
    <div ref={containerRef} className={cn('relative flex flex-col', className)}>
      {rows.map(({ item, key, leaving }) => (
        <div
          key={key}
          ref={registerRow(key)}
          /*
            A departing row is inert — on its way out, already lifted out of flow,
            and must not be tabbable, clickable or read aloud on the way.
          */
          aria-hidden={leaving || undefined}
          className={cn(leaving && 'pointer-events-none', item.className)}
        >
          {item.node}
        </div>
      ))}
    </div>
  );
}
